<template>
    <!-- PreviewTakeover —> only ever rendered inside the authenticated /user wrapper -->
    <!-- Disabled after preview→app promotion. Component kept for reference / future reuse. -->
    <!-- <PreviewTakeover v-model="showPreviewTakeover" /> -->

    <LeftNavBar />
    <MobileNavigation />
    <!-- Main App Container -->
    <div :class="[layout, 'user-container']">
        <router-view v-if="accountId" :key="accountId" />
        <router-view v-else />
    </div>

    <!-- Alert Modal -->
    <AlertModal />

    <!-- Plan enforcment Modal -->
    <PlanEnforcementModal v-if="user" />
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useTitle, useLocalStorage } from '@vueuse/core'

import { provideUser } from '@/composition/user/useUser'
import { provideAccountList } from '@/composition/user/useAccountList'
import { provideDeepLinking } from '@/composition/global/useDeepLinking'
import { layout } from '@/composition/layouts/useLayouts'

import LeftNavBar from '@/layouts/LeftNavBar.vue'
import MobileNavigation from '@/layouts/MobileNavigation.vue'
import AlertModal from '@/components/Alerts/AlertModal.vue'
import PlanEnforcementModal from '@/components/billing/PlanEnforcementModal.vue'
import PreviewTakeover from '@/components/PreviewTakeover/PreviewTakeover.vue'
import { useRouter } from 'vue-router'
import { Account } from '@opteo/types'

export default defineComponent({
    name: 'App',
    components: {
        LeftNavBar,
        MobileNavigation,
        AlertModal,
        PlanEnforcementModal,
        PreviewTakeover,
    },
    setup() {
        const { user, error } = provideUser()
        const { accountList } = provideAccountList(user)

        const router = useRouter()

        const { currentRoute } = router

        const accountId = computed(
            () => currentRoute.value.params.accountId as Account.ID | undefined
        )

        provideDeepLinking(accountId)

        const windowTitle = computed(() => {
            if (!accountList.value || !accountId) {
                return 'Opteo'
            }

            const domainRow = accountList.value.find(d => d.accountId === accountId.value)

            if (!domainRow) {
                return 'Opteo'
            }

            return `Opteo - ${domainRow.name} (${domainRow.tasksAvailable})`
        })
        // @ts-expect-error
        useTitle(windowTitle)

        // PreviewTakeover —> persist the dismissal timestamp in localStorage and
        // re-show the takeover once the dismissal is older than 72 hours.
        const PREVIEW_TAKEOVER_TTL = 72 * 60 * 60 * 1000 // 72 hours in ms
        const previewTakeoverDismissedAt = useLocalStorage<number | null>(
            'preview-takeover-dismissed-at',
            null
        )
        const showPreviewTakeover = computed<boolean>({
            get() {
                const dismissedAt = previewTakeoverDismissedAt.value
                if (!dismissedAt) return true
                return Date.now() - dismissedAt > PREVIEW_TAKEOVER_TTL
            },
            set(value) {
                // Only the close action (value === false) is meaningful here —
                // stamp the dismissal time so the takeover stays hidden for 72h.
                if (!value) previewTakeoverDismissedAt.value = Date.now()
            },
        })

        return {
            user,
            error,
            layout,
            accountId,
            showPreviewTakeover,
        }
    },
})
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
.full-screen.user-container {
    width: 100vw;
    margin-left: 0;
}
@media (min-width: $mq-768-min) {
    .user-container {
        // accounts for width of .left-nav-bar
        margin-left: 6rem;
    }
}
</style>
